import {Filter, FilterQuery, type FilterProvider} from '@github-ui/filter'
import {useCallback, useMemo, type FC} from 'react'
import {EvaluateStatusFilterProvider} from './providers/evaluate-status'
import {RulesetFilterProvider} from './providers/ruleset'
import {UserFilterProvider} from './providers/user'
import {TimePeriodFilterProvider} from './providers/time-period'
import {RuleStatusFilterProvider} from './providers/rule-status'
import type {EvaluateStatus, InsightsFilter, Ruleset, RuleStatus, TimePeriod, SourceType} from '../../types/rules-types'
import {useRelativeNavigation} from '../../hooks/use-relative-navigation'
import {insightsIndexPath} from '../../helpers/insights-filter'
import {OrgFilterProvider, RepositoryFilterProvider} from '@github-ui/filter/providers'
import {ShaFilterProvider} from './providers/sha'

export const convertQueryToInsightsFilter = (query: string, rulesets: Ruleset[]): InsightsFilter => {
  const filter: InsightsFilter = {}

  const evaluateStatusMatch = query.match(/evaluate-status:(all|active|evaluate)/)
  if (evaluateStatusMatch) {
    filter.evaluateStatus = evaluateStatusMatch[1] as EvaluateStatus
  }

  const rulesetMatch = query.match(/ruleset:"(.+?)"/)
  if (rulesetMatch) {
    filter.ruleset = rulesets.find(ruleset => ruleset.name === rulesetMatch[1])
  }

  const userMatch = query.match(/user:(\S+)/)
  if (userMatch && userMatch[1]) {
    filter.actor = {login: userMatch[1], name: '', path: '', primaryAvatarUrl: ''}
  }

  const timePeriodMatch = query.match(/time-period:(hour|day|week|month)/)
  if (timePeriodMatch) {
    filter.timePeriod = timePeriodMatch[1] as TimePeriod
  }

  const ruleStatusMatch = query.match(/rule-status:(all|pass|fail|bypass)/)
  if (ruleStatusMatch) {
    filter.ruleStatus = ruleStatusMatch[1] as RuleStatus
  }

  const shaMatch = query.match(/sha:(\S+)/)
  if (shaMatch && shaMatch[1]) {
    filter.afterOid = shaMatch[1]
  }

  return filter
}

export type InsightsFilterBar2Props = {
  rulesets: Ruleset[]
  sourceType: SourceType
  businessSlug?: string
}

export const InsightsFilterBar2: FC<InsightsFilterBar2Props> = ({rulesets, sourceType, businessSlug}) => {
  const {navigate} = useRelativeNavigation()

  const filterData = useCallback(
    (request: FilterQuery) => {
      const query = request.raw
      const insightsFilter = convertQueryToInsightsFilter(query, rulesets)
      insightsFilter.page = undefined // Reset to first page when filter changes
      navigate('.', insightsIndexPath({filter: insightsFilter}), true)
    },
    [navigate, rulesets],
  )

  const filterProviders = useMemo(() => {
    const providers: FilterProvider[] = [
      new EvaluateStatusFilterProvider(),
      new RulesetFilterProvider(rulesets),
      new UserFilterProvider(),
      new TimePeriodFilterProvider(),
      new RuleStatusFilterProvider(),
    ]

    if (sourceType === 'repository') {
      providers.unshift(new ShaFilterProvider())
    }

    if (sourceType === 'organization') {
      providers.unshift(
        new RepositoryFilterProvider({
          filterTypes: {
            exclusive: false,
            valueless: false,
            hasValue: false,
            multiKey: false,
            multiValue: false,
          },
        }),
      )
    }

    if (sourceType === 'enterprise' && businessSlug) {
      providers.unshift(
        new OrgFilterProvider({
          businessSlug,
          filterTypes: {
            exclusive: false,
            valueless: false,
            hasValue: false,
            multiKey: false,
            multiValue: false,
          },
        }),
      )
    }

    return providers
  }, [rulesets, sourceType, businessSlug])

  return (
    <Filter
      id="repos-insights-filter"
      label="Filter repos insights"
      onSubmit={filterData}
      providers={filterProviders}
    />
  )
}

try{ InsightsFilterBar2.displayName ||= 'InsightsFilterBar2' } catch {}